import { venueZone } from "../format.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AUDIT_KINDS,
  auditHeadline,
  describeChange,
  money,
  type AuditChange,
  type AuditEntry,
  type AuditKind,
  type AuditValue,
} from "@baros/core";
import { useApp } from "../context.js";
import { request, type OrgMember, type OrgVenue } from "./api.js";

const KIND_LABELS: Readonly<Record<AuditKind, string>> = {
  stock: "Stock",
  price: "Prices",
  spec: "Specs",
  order: "Orders",
  wastage: "Wastage",
  cashup: "Cash-ups",
  people: "People",
  settings: "Limits",
};

/** Detail fields that hold somebody's user id, shown as their name. */
const PERSON_FIELDS: ReadonlySet<string> = new Set([
  "approvedBy",
  "countedBy",
  "createdBy",
  "loggedBy",
]);

const PAGE = 50;

const activityApi = {
  page: (token: string, query: { venueId: string; kind: string; cursor: string | null }) => {
    const params = new URLSearchParams({ limit: String(PAGE) });
    if (query.venueId) params.set("venueId", query.venueId);
    if (query.kind) params.set("kind", query.kind);
    if (query.cursor) params.set("cursor", query.cursor);
    return request<{ entries: readonly AuditEntry[]; cursor: string | null }>(
      token,
      "GET",
      `/v1/org/activity?${params.toString()}`,
    );
  },
};

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", { timeZone: venueZone(),
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Screen 12 › Activity: who changed what, newest first (#89).
 *
 * Read-only, and only ever appended to — there is nothing here to edit or
 * delete, which is the point of it. Ids in an entry are shown by name where
 * the screen already knows the name (the library, the org's venues and
 * people), and as the id otherwise: an ingredient deleted since, or a person
 * removed, still reads as something rather than a blank. The actor's name is
 * the one stored with the entry, as they were called at the time.
 */
export function Activity({
  venues,
  members,
}: {
  readonly venues: readonly OrgVenue[];
  readonly members: readonly OrgMember[];
}) {
  const app = useApp();
  const [venueId, setVenueId] = useState("");
  const [kind, setKind] = useState("");
  const [entries, setEntries] = useState<readonly AuditEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The request whose answer the screen is waiting for. Changing a filter, or
  // asking for older entries, starts a new one, and an answer to any earlier
  // request is dropped — otherwise rows from the old filter could land in, or
  // be appended to, the new list.
  const latest = useRef(0);

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of app.library.ingredients) map.set(i.ingredientId, i.name);
    for (const s of app.library.specs) map.set(s.specId, s.name);
    for (const v of venues) map.set(v.venueId, v.name);
    for (const m of members) map.set(m.userId, m.displayName || m.email);
    return map;
  }, [app.library, venues, members]);
  const nameOf = useCallback((id: string) => names.get(id) ?? id, [names]);

  const load = useCallback(
    async (from: string | null) => {
      const id = ++latest.current;
      setBusy(true);
      setError(null);
      try {
        const page = await activityApi.page(app.token, { venueId, kind, cursor: from });
        if (id !== latest.current) return;
        setEntries((prev) => (from && prev ? [...prev, ...page.entries] : page.entries));
        setCursor(page.cursor);
      } catch (err) {
        if (id !== latest.current) return;
        setError(err instanceof Error ? err.message : "Could not read the activity");
      } finally {
        if (id === latest.current) setBusy(false);
      }
    },
    [app.token, venueId, kind],
  );

  // A new filter starts the list again from the newest entry.
  useEffect(() => {
    setEntries(null);
    setCursor(null);
    void load(null);
  }, [load]);

  const showValue = (field: string, value: AuditValue | undefined): AuditValue | undefined => {
    if (typeof value === "string" && PERSON_FIELDS.has(field)) return nameOf(value);
    if (typeof value === "number" && field.endsWith("Cents")) return money(value / 100);
    return value;
  };
  const showChange = (change: AuditChange): string =>
    describeChange({
      ...change,
      ...(change.item !== undefined ? { item: nameOf(change.item) } : {}),
      ...("before" in change ? { before: showValue(change.field, change.before) } : {}),
      ...("after" in change ? { after: showValue(change.field, change.after) } : {}),
    });

  return (
    <div className="org-section">
      <h3>Activity</h3>
      <div className="org-note">
        Who changed what, and when: stock levels and counts, prices and specs, order and wastage
        approvals, cash-ups, approval limits, and changes to people and their access. Kept for good
        — nothing here can be edited or deleted.
      </div>

      <div className="row activity-filters">
        <label className="field">
          <span className="label">Venue</span>
          <select value={venueId} onChange={(e) => setVenueId(e.target.value)}>
            <option value="">All venues</option>
            {venues.map((v) => (
              <option key={v.venueId} value={v.venueId}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Everything</option>
            {AUDIT_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="msg err">{error}</div>}
      {entries === null && !error && <div className="label">Reading the activity…</div>}
      {/* A filtered read stops after so many entries even when it found
          none, and hands back where it got to: that is "none yet", not
          "none at all". */}
      {entries?.length === 0 && (
        <div className="org-note">
          {cursor
            ? "Nothing matches those filters in the most recent stretch of the log. Older entries are still to be searched."
            : venueId || kind
              ? "Nothing matches those filters."
              : "Nothing recorded yet."}
        </div>
      )}

      {entries && entries.length > 0 && (
        <div className="table">
          {entries.map((e) => {
            const target = e.target.label || nameOf(e.target.id);
            const detail = Object.entries(e.detail).filter(([, v]) => v !== null && v !== "");
            return (
              <div className="trow org-trow activity-row" key={`${e.at}#${e.auditId}`}>
                <span className="m-name">
                  <span className="n">
                    {auditHeadline(e)}
                    {target && <span className="muted"> · {target}</span>}
                  </span>
                  <span className="s">
                    {when(e.at)} · {e.actorName}
                    {e.deviceId ? " on a tablet" : ""}
                    {e.venueId ? ` · ${nameOf(e.venueId)}` : ""}
                  </span>
                </span>
                {(e.changes.length > 0 || detail.length > 0) && (
                  <ul className="activity-changes">
                    {e.changes.map((change, i) => (
                      <li key={`c${i}`}>{showChange(change)}</li>
                    ))}
                    {e.truncated ? <li className="muted">and {e.truncated} more</li> : null}
                    {detail.map(([field, value]) => (
                      <li key={`d${field}`} className="muted">
                        {field}: {String(showValue(field, value))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {cursor && (
        <div className="row">
          <button className="btn" disabled={busy} onClick={() => void load(cursor)}>
            {busy ? "Reading…" : entries?.length === 0 ? "Keep looking" : "Older"}
          </button>
        </div>
      )}
    </div>
  );
}
